
import { toggleDebug } from 'api/AuthContext';
import ErrorBoundary from 'components/custom/ErrorBoundary';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTable, useSortBy, usePagination, ColumnWithLooseAccessor, useGlobalFilter, ColumnWithStrictAccessor, Column } from 'react-table';
import { Button, Card, CardBody, CardFooter, CardHeader, CardTitle, Input, Nav, Pagination, PaginationItem, PaginationLink, Popover, PopoverBody, Table, Tooltip } from 'reactstrap';
import RowActionItem, { RowAction } from './RowActionItem';


export interface DataTableProps {
  title?: string;
  subtitle?: string;
  data?: Array<any>;
  columns: Array<any>; // Array<ColumnWithLooseAccessor | ColumnWithStrictAccessor>;
  actions?: Array<RowAction>;
  pageSize?: number;
  initialSort?: string;
  ascending?: boolean;
  filterable?: boolean;
}

function DataTable({ title, subtitle, data = [], columns, actions = [], pageSize = 10, initialSort = '', ascending, filterable = true }: DataTableProps) {
  const tableInstance = useTable({ 
      columns, 
      data, 
      initialState: {
        pageSize,
        sortBy: [{
          id: initialSort,
          desc: !ascending
        }]
      } 
    }, 
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    toggleSortBy,
    page,
    prepareRow,
    pageCount,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setGlobalFilter,
    state: { globalFilter }
  } = tableInstance;

  return (
    <Card>
      { Boolean(title || subtitle || filterable) &&
        <CardHeader>
          { Boolean(title) && <CardTitle>{ title }</CardTitle> }
          { Boolean(subtitle) && <p className='category'>{ subtitle }</p> }
          { Boolean(filterable) && 
            <div>
              <Input 
                value={globalFilter} 
                onChange={e => setGlobalFilter(e.currentTarget.value)} 
                placeholder='Search...'
                /> 
            </div>
          }
        </CardHeader>
      }
      <CardBody>
        <Table className="tablesorter" responsive {...getTableProps()}>
          <thead>
            { headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                { headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>
                    <span color='link' onClick={() => toggleSortBy(column.id)}>
                      { column.render('Header') }
                    </span>
                    <span>
                      { column.isSorted
                        ? column.isSortedDesc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''
                      }
                    </span>
                  </th>
                ))}
                { Boolean(actions.length) && 
                  <th className='text-right'>Actions</th>
                }
            </tr>
          ))}
        </thead>
          <tbody {...getTableBodyProps()}>
            { page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()}>
                  { row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()}>
                        { cell.render('Cell') }
                      </td>
                    );
                  })}
                  { Boolean(actions.length) && 
                    <td className='text-right d-flex'>
                      { actions
                        .filter(action => action.shouldShow ? action.shouldShow(action.name, row) : true)
                        .map((action, i) => (
                          <RowActionItem 
                            key={i}
                            action={action} 
                            row={row} 
                            />
                        ))}
                    </td>
                  }
                </tr>
              )
            })}
          </tbody>
        </Table>
      </CardBody>
      { pageCount > 1 && (
        <CardFooter>
          <Nav horizontal='center'>
            <Pagination size='lg'>
              <PaginationItem>
                <PaginationLink first onClick={() => gotoPage(0)} />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink previous onClick={() => previousPage()} disabled={!canPreviousPage} />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink disabled />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink next onClick={() => nextPage()} disabled={!canNextPage} />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink last onClick={() => gotoPage(pageCount - 1)} />
              </PaginationItem>
            </Pagination>
          </Nav>
        </CardFooter>
      )}
    </Card>
  );
}

export default DataTable;